import { useId, useState, type ReactNode } from 'react';
import { Minus, Plus } from '@phosphor-icons/react';
import { trackNewsletterFaqOpened, type NewsletterFaqId } from '@/lib/subscribeFunnelAnalytics';
import FAQDemoStage, { type FAQDemoKind } from '../components/faq-demos/FAQDemoStage';

interface FAQItem {
  id: NewsletterFaqId;
  question: string;
  answer: ReactNode;
  example?: { kind: FAQDemoKind; label: string };
}

const faqItems: FAQItem[] = [
  {
    id: 'email_platforms',
    question: 'Will it work with my newsletter platform?',
    answer: (
      <>
        Kit has a native Atrivial Quiz block. With beehiiv, Ghost, Mailchimp, or MailerLite, add the
        first question through a custom HTML block. Substack uses a quiz link instead, because its
        editor does not accept custom HTML.
      </>
    ),
  },
  {
    id: 'email_answers',
    question: 'What happens when a reader clicks an answer?',
    answer: (
      <>
        The first question appears in your email. Clicking an answer opens your branded quiz page,
        where readers see their result and keep playing. They can play without creating an account.
      </>
    ),
    example: { kind: 'reader', label: 'Try the reader experience' },
  },
  {
    id: 'editing_questions',
    question: 'Can I review and edit before publishing?',
    answer: (
      <>
        Yes. Edit generated questions, answers, and fun facts before they go live. For recurring
        quizzes, choose Review before publishing to approve each new edition. If you do not approve
        it, the current quiz stays live. Library questions cannot be edited.
      </>
    ),
    example: { kind: 'editor', label: 'Try editing a question' },
  },
  {
    id: 'ongoing_quizzes',
    question: 'How much work does each newsletter require?',
    answer: (
      <>
        Add your content, review your first quiz, and place it in your newsletter. An RSS feed can
        supply new content on your chosen schedule. Auto-Updating Email is installed once and
        follows your live quiz. Fixed Email HTML needs copying again for each new edition.
      </>
    ),
  },
  {
    id: 'plan_limits',
    question: 'Which plan do I need?',
    answer: (
      <>
        Individual includes email quizzes, scheduling, your branding, and analytics. Business adds
        website embeds on up to two websites, sponsor placements, and more AI capacity. AI
        generation has an included allowance, with usage shown in your Portal.{' '}
        <a href="#pricing" className="newsletter-faq-link">
          Compare plans
        </a>
        .
      </>
    ),
  },
  {
    id: 'trial_and_cancellation',
    question: 'Can I try it before paying, and cancel anytime?',
    answer: (
      <>
        Yes. Individual and Business include a free trial. A payment method is required; checkout
        shows the trial length and first charge. Cancel in the Portal before the trial ends to avoid
        a charge. After that, cancellation stops renewal and Portal access continues until the end
        of your current billing period.
      </>
    ),
  },
];

export default function FAQ() {
  const [activeId, setActiveId] = useState<NewsletterFaqId | null>(null);
  const [exampleOpen, setExampleOpen] = useState(false);
  const id = useId();

  const toggleQuestion = (questionId: NewsletterFaqId) => {
    const next = activeId === questionId ? null : questionId;
    setActiveId(next);
    setExampleOpen(false);
    if (next) trackNewsletterFaqOpened(next);
  };

  return (
    <section data-newsletter-section="faq" id="faq" className="bg-nl-paper px-6 py-16 md:py-20">
      <div className="newsletter-faq-layout mx-auto max-w-6xl">
        <h2 className="h-display text-4xl leading-tight font-medium md:text-5xl">FAQ</h2>
        <div className="newsletter-faq-list">
          {faqItems.map((item) => {
            const open = activeId === item.id;
            const questionId = `${id}-${item.id}`;
            const answerId = `${questionId}-answer`;
            const exampleId = `${questionId}-example`;
            return (
              <div key={item.id} className="newsletter-faq-item">
                <h3>
                  <button
                    id={questionId}
                    type="button"
                    aria-expanded={open}
                    aria-controls={answerId}
                    onClick={() => toggleQuestion(item.id)}
                    className="newsletter-faq-question"
                  >
                    <span>{item.question}</span>
                    {open ? (
                      <Minus size={20} aria-hidden="true" />
                    ) : (
                      <Plus size={20} aria-hidden="true" />
                    )}
                  </button>
                </h3>
                <div
                  id={answerId}
                  role="region"
                  aria-labelledby={questionId}
                  hidden={!open}
                  className="newsletter-faq-answer-space"
                >
                  {open && (
                    <>
                      <p className="newsletter-faq-answer">{item.answer}</p>
                      {item.example && (
                        <>
                          <button
                            type="button"
                            aria-expanded={exampleOpen}
                            aria-controls={exampleId}
                            className="newsletter-faq-example-toggle"
                            onClick={() => setExampleOpen((value) => !value)}
                          >
                            {exampleOpen ? 'Hide example' : item.example.label}
                          </button>
                          <div
                            id={exampleId}
                            hidden={!exampleOpen}
                            className="newsletter-faq-example"
                          >
                            {exampleOpen && <FAQDemoStage kind={item.example.kind} />}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
